import { action, computed, makeObservable, observable } from "mobx"

export type WithdrawState =
  | "idle"
  | "checking-expired"
  | "not-expired"
  | "expired"
  | "checking-reset-required"
  | "reset-required"
  | "resetting"
  | "reset-failed"
  | "withdraw-allowed"
  | "withdrawing"
  | "withdraw-failed"
  | "withdrawn"

export interface IWithdrawLock {
  id: number
  lockAmount: number
  hasExpired: boolean
  needsResetToWithdraw: boolean
  canWithdraw: boolean
  displayLockEnds: string
  displayLockAmount: string
}

export interface WithdrawStoreDAO {
  state: WithdrawState
  lock: IWithdrawLock | null
  resetError: string | null
  withdrawError: string | null
  resetTxHash: string | null
  withdrawTxHash: string | null
}

export class WithdrawStore {
  state: WithdrawState = "idle"
  lock: IWithdrawLock | null = null
  resetError: string | null = null
  withdrawError: string | null = null
  resetTxHash: string | null = null
  withdrawTxHash: string | null = null

  constructor(data?: Partial<WithdrawStoreDAO>) {
    if (data) {
      this.state = data.state ?? "idle"
      this.lock = data.lock ?? null
      this.resetError = data.resetError ?? null
      this.withdrawError = data.withdrawError ?? null
      this.resetTxHash = data.resetTxHash ?? null
      this.withdrawTxHash = data.withdrawTxHash ?? null
    }

    makeObservable(this, {
      lock: observable,
      resetError: observable,
      resetTxHash: observable,
      state: observable,
      withdrawError: observable,
      withdrawTxHash: observable,
      clearAllErrors: action,
      clearResetError: action,
      clearWithdrawError: action,
      hasError: computed,
      isInProgress: computed,
      reset: action,
      resetTxHashUrl: computed,
      setLock: action,
      setResetError: action,
      setResetTxHash: action,
      setState: action,
      setWithdrawError: action,
      setWithdrawTxHash: action,
      withdrawTxHashUrl: computed,
    })
  }

  clearAllErrors(): void {
    this.resetError = null
    this.withdrawError = null
  }

  clearResetError(): void {
    this.resetError = null
  }

  clearWithdrawError(): void {
    this.withdrawError = null
  }

  setLock(lock: IWithdrawLock | null): void {
    this.lock = lock
  }

  setResetError(error: string): void {
    this.resetError = error
  }

  setResetTxHash(txHash: string): void {
    this.resetTxHash = txHash
  }

  setState(state: WithdrawState): void {
    this.state = state
  }

  setWithdrawError(error: string): void {
    this.withdrawError = error
  }

  setWithdrawTxHash(txHash: string): void {
    this.withdrawTxHash = txHash
  }

  reset(): void {
    this.state = "idle"
    this.resetError = null
    this.withdrawError = null
    this.resetTxHash = null
    this.withdrawTxHash = null
  }

  get resetTxHashUrl(): string {
    if (!this.resetTxHash) return ""
    return `https://basescan.org/tx/${this.resetTxHash}`
  }

  get withdrawTxHashUrl(): string {
    if (!this.withdrawTxHash) return ""
    return `https://basescan.org/tx/${this.withdrawTxHash}`
  }

  get hasError(): boolean {
    return this.resetError !== null || this.withdrawError !== null
  }

  get isInProgress(): boolean {
    return [
      "checking-expired",
      "expired",
      "checking-reset-required",
      "resetting",
      "withdrawing",
    ].includes(this.state)
  }
}

